import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PhotoIcon,
  VideoCameraIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  HomeIcon,
  AcademicCapIcon,
  FlagIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ArrowLeftIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

// Import gallery images
import campusImage from '../../assets/update/Campus.jpeg';
import openHouseImage from '../../assets/update/openhouse2.jpg';
import sessionImage from '../../assets/update/SessionbyPirzada.jpeg';
import independenceImage from '../../assets/update/openhouse.jpeg';
import peopleImage from '../../assets/update/PeopleatAureus.jpeg';
import galleryHeroImage from '../../assets/update/Our gallery Hero.jpeg';

// Import JSON data
import defenceDayData from '../../data/Defence day/defenceDay.json';
import teachersDayData from '../../data/Teachers day/teachersDay.json';
import independenceDayData from '../../data/Independence day/independenceDay.json';
import sessionPirzadaData from '../../data/Special Session by Dr. Pirzada Qasim/sessionPirzada.json';
import meetThePeopleData from '../../data/Meet the People/meetThePeople.json';
import campusData from '../../data/Campus/campus.json';

const Gallery = () => {
  const [currentView, setCurrentView] = useState('sections'); // 'sections' or 'media'
  const [activeSection, setActiveSection] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Handle browser back button navigation
  useEffect(() => {
    const handlePopState = (event) => {
      if (selectedMedia) {
        // If modal is open, close it
        setSelectedMedia(null);
      } else if (currentView === 'media') {
        // If viewing media, go back to sections
        setCurrentView('sections');
        setActiveSection(null);
      }
      // Otherwise, allow normal browser navigation
    };

    // Add event listener for back button
    window.addEventListener('popstate', handlePopState);

    // Push state when entering media view or modal
    // Removed to prevent page refresh issues

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [currentView, selectedMedia]);

  // Gallery sections with their respective content
  const gallerySections = [
    {
      id: 'campus',
      title: 'Campus',
      icon: BuildingOfficeIcon,
      description: 'Explore our beautiful campus facilities and learning spaces',
      bgImage: campusImage,
      photos: campusData.photos,
      videos: campusData.videos.map(video => ({
        ...video,
        thumbnail: require('../../assets/building.jpg')
      }))
    },
    {
      id: 'openhouse',
      title: 'Open House',
      icon: HomeIcon,
      description: 'Open house events and parent-school interactions',
      bgImage: openHouseImage,
      photos: [
        { id: 7, src: require('../../assets/images/Open House/01 (10).jpg'), alt: 'Open House Event' },
        { id: 8, src: require('../../assets/images/Open House/01 (14).jpg'), alt: 'Parent Meeting' },
        { id: 9, src: require('../../assets/images/Open House/01 (15).jpg'), alt: 'Campus Tour' },
        { id: 10, src: require('../../assets/images/Open House/01 (18).jpg'), alt: 'Information Session' },
        { id: 11, src: require('../../assets/images/Open House/01 (2).jpg'), alt: 'Open House Activity' },
        { id: 12, src: require('../../assets/images/Open House/01 (22).jpg'), alt: 'Parent Engagement' },
        { id: 13, src: require('../../assets/images/Open House/01 (32).jpg'), alt: 'School Presentation' },
        { id: 14, src: require('../../assets/images/Open House/01 (37).jpg'), alt: 'Open House Gathering' },
        { id: 15, src: require('../../assets/images/Open House/01 (40).jpg'), alt: 'Parent Discussion' },
        { id: 16, src: require('../../assets/images/Open House/01 (42).jpg'), alt: 'Open House Event' },
        { id: 17, src: require('../../assets/images/Open House/01 (5).jpg'), alt: 'School Tour' },
        { id: 18, src: require('../../assets/images/Open House/01 (7).jpg'), alt: 'Open House Activity' },
        { id: 19, src: require('../../assets/images/Open House/01 (9).jpg'), alt: 'Parent Visit' },
        { id: 20, src: require('../../assets/images/Open House/02 (12).jpg'), alt: 'Open House Session' },
        { id: 21, src: require('../../assets/images/Open House/02 (14).jpg'), alt: 'Parent Interaction' },
        { id: 22, src: require('../../assets/images/Open House/02 (15).jpg'), alt: 'School Showcase' },
        { id: 23, src: require('../../assets/images/Open House/02 (3).jpg'), alt: 'Open House Event' },
        { id: 24, src: require('../../assets/images/Open House/02 (4).jpg'), alt: 'Parent Meeting' },
        { id: 25, src: require('../../assets/images/Open House/02 (7).jpg'), alt: 'School Visit' },
        { id: 26, src: require('../../assets/images/Open House/03 (3).jpg'), alt: 'Open House Activity' },
        { id: 27, src: require('../../assets/images/Open House/05 (1).jpg'), alt: 'Parent Engagement' },
        { id: 28, src: require('../../assets/images/Open House/06 (10).jpg'), alt: 'School Tour' },
        { id: 29, src: require('../../assets/images/Open House/06 (11).jpg'), alt: 'Open House Gathering' },
        { id: 30, src: require('../../assets/images/Open House/06 (17).jpg'), alt: 'Parent Session' },
        { id: 31, src: require('../../assets/images/Open House/07 (13).jpg'), alt: 'Open House Event' },
        { id: 32, src: require('../../assets/images/Open House/07 (14).jpg'), alt: 'School Presentation' },
        { id: 33, src: require('../../assets/images/Open House/07 (16).jpg'), alt: 'Parent Visit' },
        { id: 34, src: require('../../assets/images/Open House/07 (3).jpg'), alt: 'Open House Activity' },
        { id: 35, src: require('../../assets/images/Open House/08 (10).jpg'), alt: 'Parent Meeting' },
        { id: 36, src: require('../../assets/images/Open House/08 (3).jpg'), alt: 'School Tour' },
        { id: 37, src: require('../../assets/images/Open House/08 (7).jpg'), alt: 'Open House Session' },
        { id: 38, src: require('../../assets/images/Open House/08 (8).jpg'), alt: 'Parent Interaction' },
        { id: 39, src: require('../../assets/images/Open House/09 (13).jpg'), alt: 'Open House Event' },
        { id: 40, src: require('../../assets/images/Open House/09 (15).jpg'), alt: 'School Showcase' },
        { id: 41, src: require('../../assets/images/Open House/09 (17).jpg'), alt: 'Parent Visit' },
        { id: 42, src: require('../../assets/images/Open House/09 (2).jpg'), alt: 'Open House Activity' },
        { id: 43, src: require('../../assets/images/Open House/09 (4).jpg'), alt: 'Parent Engagement' },
        { id: 44, src: require('../../assets/images/Open House/09 (5).jpg'), alt: 'School Tour' }
      ]
    },
    {
      id: 'drpirzada',
      title: 'Special Session by Dr. Pirzada Qasim',
      icon: AcademicCapIcon,
      description: 'Educational sessions and lectures by Dr. Pirzada Qasim',
      bgImage: sessionImage,
      photos: sessionPirzadaData.photos.map(photo => ({
        ...photo,
        src: photo.src.includes('cloudinary.com')
          ? photo.src.replace('/upload/', '/upload/f_auto,q_auto,c_limit/')
          : photo.src
      })),
      videos: sessionPirzadaData.videos
    },
    {
      id: 'independence',
      title: 'Independence Day',
      icon: FlagIcon,
      description: 'Celebrating Pakistan Independence Day with patriotic fervor',
      bgImage: independenceImage,
      photos: independenceDayData.photos.map(photo => ({
        ...photo,
        src: photo.src.includes('cloudinary.com')
          ? photo.src.replace('/upload/', '/upload/f_auto,q_auto,c_limit/')
          : photo.src
      })),
      videos: independenceDayData.videos
    },
    {
      id: 'people',
      title: 'Meet the People at Aureus',
      icon: UserGroupIcon,
      description: 'Our dedicated faculty, staff, and the Aureus family',
      bgImage: peopleImage,
      photos: meetThePeopleData.photos,
      videos: meetThePeopleData.videos.map(video => ({
        ...video,
        thumbnail: require('../../assets/update/PeopleatAureus.jpeg')
      }))
    },
    {
      id: 'defenceday',
      title: 'Defence Day',
      icon: ShieldCheckIcon,
      description: 'Pakistan Defence Day celebration at Aureus Cambridge School',
      bgImage: independenceImage, // Use independence image as background since it's similar theme
      photos: defenceDayData.photos.map(photo => ({
        ...photo,
        src: photo.src.includes('cloudinary.com')
          ? photo.src.replace('/upload/', '/upload/f_auto,q_auto,c_limit/').replace('.heic', '.jpg')
          : photo.src
      })),
      videos: defenceDayData.videos
    },
    {
      id: 'teachersday',
      title: 'Teachers Day',
      icon: AcademicCapIcon,
      description: 'Celebrating our dedicated educators at Aureus Cambridge School',
      bgImage: teachersDayData.photos[0]?.src || sessionImage, // Use first Teachers Day photo as background
      photos: teachersDayData.photos.filter(photo => photo.src).map(photo => ({
        ...photo,
        src: photo.src.includes('cloudinary.com')
          ? photo.src.replace('/upload/', '/upload/f_auto,q_auto,c_limit/')
          : photo.src
      })), // Only include photos with src
      videos: teachersDayData.videos.filter(video => video.src) // Only include videos with src
    }
  ];

  // Navigate to section media view
  const enterSection = useCallback((section) => {
    setActiveSection(section);
    setCurrentView('media');
  }, []);

  // Go back to sections overview
  const backToSections = useCallback(() => {
    setCurrentView('sections');
    setActiveSection(null);
  }, []);

  // Open media in modal
  const openMediaModal = useCallback((media) => {
    setSelectedMedia(media);
  }, []);

  // Close media modal
  const closeMediaModal = useCallback(() => {
    setSelectedMedia(null);
  }, []);

  // Get all media items for current section for modal navigation
  const getCurrentSectionMedia = () => {
    if (!activeSection) return [];

    let allMedia = [];
    // Add all photos
    if (activeSection.photos && Array.isArray(activeSection.photos)) {
      activeSection.photos.forEach(photo => {
        if (photo && photo.src) {
          allMedia.push({ ...photo, type: 'photo' });
        }
      });
    }
    // Add all videos
    if (activeSection.videos && Array.isArray(activeSection.videos)) {
      activeSection.videos.forEach(video => {
        if (video && video.src) {
          allMedia.push({ ...video, type: 'video' });
        }
      });
    }
    return allMedia;
  };

  // Navigate to previous/next media in modal
  const navigateMedia = (direction) => {
    const allMedia = getCurrentSectionMedia();
    const currentIndex = allMedia.findIndex(item =>
      item.id === selectedMedia.id && item.type === selectedMedia.type
    );

    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : allMedia.length - 1;
    } else {
      newIndex = currentIndex < allMedia.length - 1 ? currentIndex + 1 : 0;
    }

    const newMedia = allMedia[newIndex];
    if (newMedia) {
      setSelectedMedia(newMedia);

      // Preload next/previous images for smoother navigation
      const preloadIndexes = [
        (newIndex + 1) % allMedia.length,
        (newIndex - 1 + allMedia.length) % allMedia.length
      ];

      preloadIndexes.forEach(index => {
        const mediaToPreload = allMedia[index];
        if (mediaToPreload && mediaToPreload.type === 'photo') {
          const img = new Image();
          img.src = mediaToPreload.src;
        }
      });
    }
  };

  // Media card component that shows actual images - optimized with lazy loading
  const MediaCard = ({ media, type, index }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: Math.min(index * 0.01, 0.05) }} // Reduced delay for faster loading
        onClick={() => openMediaModal({ ...media, type })}
        className="cursor-pointer group"
      >
        <div className="overflow-hidden bg-gray-200 rounded-lg aspect-video">
          {media.src ? (
            type === 'photo' ? (
              <div className="relative w-full h-full">
                {!imageLoaded && !imageError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                {!imageError && (
                  <img
                    src={media.src}
                    alt={media.alt}
                    className={`w-full h-full object-cover transition-all duration-200 transform group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'
                      }`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                    loading="lazy"
                    decoding="async"
                    style={{ imageRendering: 'optimizeQuality' }}
                  />
                )}
                {imageError && (
                  <div className="flex items-center justify-center w-full h-full bg-gray-200">
                    <PhotoIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
            ) : (
              <div className="relative flex items-center justify-center w-full h-full rounded-lg bg-gradient-to-br from-gray-800 to-gray-900">
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="p-4 rounded-full bg-white/20 backdrop-blur-sm">
                    <VideoCameraIcon className="w-12 h-12 text-white" />
                  </div>
                </div>
                {media.thumbnail && (
                  <img
                    src={media.thumbnail}
                    alt={media.title}
                    className="object-cover w-full h-full rounded-lg"
                    loading="lazy"
                  />
                )}
              </div>
            )
          ) : (
            <div className="flex items-center justify-center w-full h-full rounded-lg bg-gradient-to-br from-gray-100 to-gray-200">
              {type === 'photo' ? (
                <PhotoIcon className="w-16 h-16 text-gray-400" />
              ) : (
                <VideoCameraIcon className="w-16 h-16 text-gray-400" />
              )}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative flex items-center justify-center min-h-screen overflow-hidden">
        <div
          className="absolute inset-0 transform scale-110 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: `url('${galleryHeroImage}')`
          }}
        />
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 max-w-4xl px-4 mx-auto text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-6"
          >
            {/* <PhotoIcon className="w-20 h-20 mx-auto mb-6 text-yellow-400" /> */}
          </motion.div>

          <motion.h1
            className="mb-6 text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            OUR <span className="text-yellow-400">GALLERY</span>
          </motion.h1>

          <motion.p
            className="mb-8 text-xl text-gray-200 md:text-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Capturing moments, preserving memories, celebrating achievements
          </motion.p>
        </div>
      </section>

      <AnimatePresence mode="wait">
        {currentView === 'sections' ? (
          // Gallery Sections Cards View
          <motion.section
            key="sections"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="py-20 bg-gray-50"
          >
            <div className="container px-4 mx-auto">
              <div className="mb-16 text-center">
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl"
                >
                  Explore Our <span className="text-yellow-500">Gallery Sections</span>
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="max-w-2xl mx-auto text-lg text-gray-600"
                >
                  Click on any section below to dive into our collection of memories and moments
                </motion.p>
              </div>

              {/* Gallery Section Cards */}
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {gallerySections && gallerySections.map((section, index) => {
                  return (
                    <motion.div
                      key={section.id}
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: Math.min(index * 0.02, 0.1) }}
                      onClick={() => enterSection(section)}
                      className="relative overflow-hidden transition-all duration-500 transform bg-white shadow-lg cursor-pointer group rounded-2xl hover:shadow-2xl hover:-translate-y-2"
                    >
                      {/* Card Background */}
                      <div className="relative h-64 overflow-hidden">
                        <div
                          className="absolute inset-0 transition-transform duration-700 transform bg-cover group-hover:scale-110"
                          style={{
                            backgroundImage: `url(${section.bgImage})`,
                            backgroundPosition: section.id === 'drpirzada' ? 'center 30%' : 'center center'
                          }}
                        />
                      </div>

                      {/* Card Content */}
                      <div className="p-6">
                        <h3 className="mb-2 text-xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-yellow-600">
                          {section.title}
                        </h3>
                        <p className="mb-4 text-sm leading-relaxed text-gray-600">
                          {section.description}
                        </p>

                        {/* Explore Button */}
                        <div className="flex items-center justify-end">
                          <div className="text-sm font-medium text-yellow-500 transition-colors duration-300 group-hover:text-yellow-600">
                            Explore →
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.section>
        ) : (
          // Individual Section Media View
          <motion.section
            key="media"
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            className="py-20 bg-gray-50"
          >
            <div className="container px-4 mx-auto">
              {/* Back Button and Section Header */}
              <div className="mb-12">
                <button
                  onClick={backToSections}
                  className="flex items-center gap-2 px-4 py-2 mb-6 text-gray-700 transition-colors duration-200 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                  Back to Gallery Sections
                </button>

                {activeSection && (
                  <div className="text-center">
                    <div className={`inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-gradient-to-r ${activeSection.color}`}>
                      <activeSection.icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="mb-2 text-3xl font-bold text-gray-900">{activeSection.title}</h2>
                    <p className="max-w-2xl mx-auto text-lg text-gray-600">{activeSection.description}</p>
                  </div>
                )}
              </div>

              {/* Media Grid */}
              {activeSection && (
                <div>
                  {/* Combined Photos and Videos Grid */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {/* Photos */}
                    {activeSection.photos && activeSection.photos.map((photo, index) => (
                      <MediaCard
                        key={`photo-${photo.id}`}
                        media={photo}
                        type="photo"
                        index={index}
                      />
                    ))}
                    {/* Videos */}
                    {activeSection.videos && activeSection.videos.map((video, index) => (
                      <MediaCard
                        key={`video-${video.id}`}
                        media={video}
                        type="video"
                        index={(activeSection.photos ? activeSection.photos.length : 0) + index}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {/* Media Modal */}
      <AnimatePresence>
        {selectedMedia && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-90"
            onClick={closeMediaModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-4xl max-h-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={closeMediaModal}
                className="absolute z-10 p-2 text-white transition-all duration-200 bg-black bg-opacity-50 rounded-full top-4 right-4 hover:bg-opacity-70"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              {/* Previous Button */}
              <button
                onClick={() => navigateMedia('prev')}
                className="absolute z-10 p-3 text-white transition-all duration-200 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full left-4 top-1/2 hover:bg-opacity-70"
              >
                <ArrowLeftIcon className="w-6 h-6" />
              </button>

              {/* Next Button */}
              <button
                onClick={() => navigateMedia('next')}
                className="absolute z-10 p-3 text-white transition-all duration-200 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full right-4 top-1/2 hover:bg-opacity-70"
              >
                <ArrowRightIcon className="w-6 h-6" />
              </button>

              {/* Media Content */}
              {selectedMedia.type === 'photo' ? (
                <img
                  src={selectedMedia.src}
                  alt={selectedMedia.alt}
                  className="object-contain max-w-full max-h-full rounded-lg"
                  style={{
                    maxHeight: '80vh',
                    maxWidth: '90vw',
                    imageRendering: 'optimizeQuality'
                  }}
                  loading="eager"
                  decoding="async"
                />
              ) : selectedMedia.type === 'video' ? (
                selectedMedia.src.includes('drive.google.com') ? (
                  <iframe
                    src={selectedMedia.src}
                    className="rounded-lg"
                    style={{ width: '90vw', maxWidth: '800px', height: '56.25vw', maxHeight: '450px' }}
                    frameBorder="0"
                    allowFullScreen
                    title={selectedMedia.title}
                  />
                ) : selectedMedia.src.includes('cloudinary.com') ? (
                  <video
                    src={selectedMedia.src}
                    controls
                    className="w-full h-auto rounded-lg"
                    style={{
                      maxWidth: '90vw',
                      maxHeight: '80vh',
                      objectFit: 'contain'
                    }}
                    preload="metadata"
                  />
                ) : (
                  <video
                    src={selectedMedia.src}
                    controls
                    className="w-full h-auto rounded-lg"
                    style={{
                      maxWidth: '90vw',
                      maxHeight: '80vh',
                      objectFit: 'contain'
                    }}
                    preload="metadata"
                  />
                )
              ) : (
                // Fallback: try to determine type from file extension, Google Drive, or Cloudinary
                selectedMedia.src && (
                  selectedMedia.src.toLowerCase().includes('.mov') ||
                  selectedMedia.src.toLowerCase().includes('.mp4') ||
                  selectedMedia.src.toLowerCase().includes('.webm') ||
                  selectedMedia.src.includes('drive.google.com') ||
                  selectedMedia.src.includes('cloudinary.com/video')
                ) ? (
                  selectedMedia.src.includes('drive.google.com') ? (
                    <iframe
                      src={selectedMedia.src}
                      className="rounded-lg"
                      style={{
                        width: '90vw',
                        maxWidth: '1200px',
                        height: '50.625vw',
                        maxHeight: '675px',
                        minHeight: '400px'
                      }}
                      frameBorder="0"
                      allowFullScreen
                      title={selectedMedia.title}
                    />
                  ) : (
                    <video
                      src={selectedMedia.src}
                      controls
                      className="w-full h-auto rounded-lg"
                      style={{
                        maxWidth: '90vw',
                        maxHeight: '80vh',
                        objectFit: 'contain'
                      }}
                      preload="metadata"
                    />
                  )
                ) : (
                  <img
                    src={selectedMedia.src}
                    alt={selectedMedia.alt}
                    className="object-contain max-w-full max-h-full rounded-lg"
                    style={{ maxHeight: '80vh', maxWidth: '90vw' }}
                  />
                )
              )}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
